import { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axios";
import { ChevronLeft } from "lucide-react";
import AuthContext from "../context/AuthContext";

import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useAppSocket } from "../hooks/useAppSocket";
import { useNotificationsStream } from "../hooks/useNotificationsStream";
import useClickOutside from "../hooks/useClickOutside";
import { useModal } from "./ModalManager";

import { fetchMyFriendRequests } from "../api/UserAPI";
import { hasNotificationTarget, routeNotification } from "../utils/notificationRouting";

import FriendReqForm from "./FriendReqResponseForm";

export default function Notifications({ onClose, buttonRef }) {
  const [notifications, setNotifications] = useState([]);
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { openModal } = useModal();
  // Guards against duplicate in-flight routing for the same notification
  // (e.g. double-click on an event notification, which does an async
  // fetch before opening the modal). Doesn't block clicks on other rows.
  const routingIdsRef = useRef(new Set());
  const [nextUrl, setNextUrl] = useState(`/notifications/?limit=15`);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [lastFetchedTime, setLastFetchTime] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetched, setHasFetched] = useState(null);
  const [friendRequests, setFriendRequests] = useState([]);
  const [showFriendRequests, setShowFriendRequests] = useState(false);
  const FETCH_DELAY = 1000;
  const modalRef = useRef();
  useClickOutside([modalRef, buttonRef], onClose);

  const { wsRef, status } = useAppSocket();
  const { newNotifications } = useNotificationsStream(wsRef, { userId: user.id });

  // init load
  useEffect(() => {
    if (!token) return;

    const ac = new AbortController();
    let canceled = false;

    (async () => {
      try {
        const friendreq = await fetchMyFriendRequests(token);
        if (canceled || ac.signal.aborted) return;

        setFriendRequests(friendreq.results);
      } catch (err) {
        if (!canceled) console.error(err);
      }
    })();

    return () => {
      canceled = true;
      ac.abort();
    };
  }, [token]);

  useEffect(() => {
    if (!notifications) return;

    const markNotifsRead = async () => {
      try {
        const res = axiosInstance.post(
          `/notifications/mark-read/`,
          {},
          {
            headers: { Authorization: `Token ${token}` },
          },
        );
      } catch (err) {
        console.error("Error marking notifications as read");
      }
    };
    markNotifsRead();
  }, [notifications]);

  useEffect(() => {
    function addMany(prev, incoming) {
      const seen = new Set(prev.map((n) => n.id));
      const toAdd = incoming.filter((n) => !seen.has(n.id));
      if (toAdd.length === 0) return prev;
      return [...toAdd, ...prev].slice(0, 50);
    }

    setNotifications((prev) => addMany(prev, newNotifications));
  }, [newNotifications]);

  const handleLoadNotifications = useCallback(() => {
    const now = Date.now();
    if (!nextUrl || loading || now - lastFetchedTime < FETCH_DELAY) return;
    if (hasMore && hasFetched) {
      setPage((prev) => prev + 1);
      setHasFetched(false);
    }
  });

  const loaderRef = useInfiniteScroll(handleLoadNotifications);

  const fetchPrevNotifications = async () => {
    if (!nextUrl || loading) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(nextUrl, {
        headers: { Authorization: `Token ${token}` },
      });
      setNotifications((prev) => [...prev, ...res.data.results]);
      setNextUrl(res.data.next);
      setHasMore(!!res.data.next);
    } catch (err) {
      console.error("Error failed to get notifications", err);
    } finally {
      setHasFetched(true);
      setLastFetchTime(Date.now());
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPrevNotifications();
    }
  }, [page]);

  const fetchFriendRequests = async () => {
    const friendreq = await fetchMyFriendRequests(token);
    setFriendRequests(friendreq.results);
  };

  // Mark-as-read is intentionally NOT tied to this click — it already
  // happens automatically (see the effect above, keyed on the
  // `notifications` list itself), so navigation here never depends on a
  // mark-read request succeeding.
  const handleNotificationClick = async (notification) => {
    if (routingIdsRef.current.has(notification.id)) return;
    routingIdsRef.current.add(notification.id);
    try {
      await routeNotification(notification, { navigate, openModal, token });
      onClose?.();
    } finally {
      routingIdsRef.current.delete(notification.id);
    }
  };

  if (notifications.length == 0) {
    return (
      <div className="flex flex-col z-[9997] space-y-6 h-screen fixed w-72 p-4 border border-gray-300 bg-white sm:ml-20 xl:ml-56 transition-[transform,width] duration-200 ease-out">
        <h2 className="text-lg font-bold mb-4">Notifications</h2>
        {loading ? (
          <p className="text-sm font-semibold text-gray-600">loading...</p>
        ) : (
          <p className="text-sm font-semibold text-gray-600">No Notifications...</p>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col z-[9997] space-y-6 h-screen fixed w-72 p-4 border border-gray-300 bg-white overflow-y-scroll sm:ml-20 xl:ml-56 transition-[transform,width] duration-200 ease-out"
      ref={modalRef}
    >
      {showFriendRequests ? (
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center justify-center"
            aria-label="Back"
            onClick={() => {
              setShowFriendRequests((prev) => !prev);
              fetchFriendRequests();
            }}
          >
            <ChevronLeft />
          </button>
          <h2 className="text-lg font-bold">Friend Requests</h2>
        </div>
      ) : (
        <h2 className="text-lg font-bold mb-4">Notifications</h2>
      )}
      {!showFriendRequests && (
        <button
          onClick={() => setShowFriendRequests((prev) => !prev)}
          className="relative rounded-full border border-black px-4 py-2 hover:bg-blue-500 hover:text-white transition-colors"
        >
          Friend Requests
          {friendRequests.length > 0 && (
            <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-purple-500 text-xs font-medium text-white">
              {friendRequests.length}
            </span>
          )}
        </button>
      )}
      {showFriendRequests ? (
        <>
          {friendRequests.length > 0 ? (
            <>
              {friendRequests.map((req) => (
                <FriendReqForm request={req} key={req.id} />
              ))}
            </>
          ) : (
            <p className="ml-4 text-sm font-semibold text-gray-600">No Friend Requests</p>
          )}
        </>
      ) : (
        <>
          {notifications.map((notification) => {
            // No target (e.g. event_deleted, or a deleted event's older
            // notifications): plain text, nothing to open.
            const routable = hasNotificationTarget(notification);
            return (
            <div
              key={notification.id}
              {...(routable
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: () => handleNotificationClick(notification),
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }
                    },
                    className: "cursor-pointer",
                  }
                : {})}
            >
              <div className="flex space-x-2 items-start">
                <img
                  src={
                    // sender is null for system notifications, deleted accounts or a missing profile.
                    notification.sender?.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`
                  }
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover border border-black flex-shrink-0"
                />
                <div
                  className={`p-2 rounded-lg max-w-xs bg-blue-400 ${
                    routable ? "hover:bg-black hover:text-white" : ""
                  }`}
                >
                  <p className="text-sm">{notification.message}</p>
                  <p className="text-[11px] text-gray-100">
                    {new Date(notification.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            );
          })}
        </>
      )}
      {/* <h2 className="text-lg font-bold mb-4">Notifications</h2>
      <>
        {notifications.map((notification) => (
          <Link key={notification.id} to={notification.url}>
            <div className="flex space-x-2 items-start">
              <img
                src={notification.sender.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover border border-black flex-shrink-0"
              />
              <div className="p-2 rounded-lg max-w-xs bg-blue-400 hover:bg-black hover:text-white">
                <p className="text-sm">{notification.message}</p>
                <p className="text-[11px] text-gray-100">
                  {new Date(notification.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </Link>
        ))} 
      </>*/}
      <div ref={loaderRef} className="h-[1px]" />
    </div>
  );
}
